import { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { useAuthState } from '../../contexts/AuthenticationContext/AuthenticationContext.hooks';
import { useCampaign } from '../../contexts/useCampaign';
import { CampaignAPI } from '../../services/api';
import { Campaign } from '../../types';
import { CAMPAIGN_TYPE_OPTIONS } from '../../utils/campaignTypes';
import { apiErrorMessage } from '../../utils/apiError';

export default function CampaignTypeEditor({ campaign, onSaved }: {
  campaign: Campaign;
  onSaved: (campaign: Campaign) => void;
}) {
  const { isAdmin } = useAuthState();
  const { selectedCampaign, setSelectedCampaign, refreshCampaigns } = useCampaign();
  const [type, setType] = useState(campaign.type);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await CampaignAPI.updateType(campaign.id, type);
      if (selectedCampaign?.id === data.id) setSelectedCampaign(data);
      onSaved(data);
      await refreshCampaigns();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to update campaign type.'));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return <p><strong>Type:</strong> {campaign.type}</p>;

  return (
    <div className="mb-3">
      <Form.Group controlId={`campaign-type-${campaign.id}`}>
        <Form.Label>Campaign type</Form.Label>
        <div className="d-flex gap-2">
          <Form.Select value={type} disabled={saving} onChange={e => setType(e.target.value as Campaign['type'])}>
            {CAMPAIGN_TYPE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Form.Select>
          <Button onClick={save} disabled={saving || type === campaign.type}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
        <Form.Text>Existing records and pending uploads must match the selected type.</Form.Text>
      </Form.Group>
      {error && <Alert variant="danger" className="mt-2 mb-0">{error}</Alert>}
    </div>
  );
}
